import { useEffect, useState, type RefObject } from "react";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBottomRightExclusion } from "@/components/layout/useBottomRightExclusion";
import { CONTROL_FIELD_CLASS } from "@/components/ui/control-scale";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DocumentHistoryPanelBody,
  type DocumentHistoryPanelView,
} from "@/domains/documents/components/DocumentHistoryPanelBody";
import { DocumentVersionHistory } from "@/domains/documents/components/DocumentVersionHistory";
import { DocumentViewerDialog } from "@/domains/documents/components/DocumentViewerDialog";
import type { DocumentHistoryEntry } from "@/domains/documents/history/documentHistory";
import { DocumentHistoryFilterControl } from "@/domains/documents/components/DocumentHistoryFilterControl";
import {
  canFetchDocumentHistory,
  useDocumentHistory,
} from "@/domains/documents/hooks/useDocumentHistory";
import {
  useClearOnScopeChange,
  useHistoryFilters,
} from "@/domains/documents/hooks/useDocumentHistoryFilters";
import { useDocumentViewer } from "@/domains/documents/hooks/useDocumentViewer";
import { isDwgParentDoc, useDrawingViewer } from "@/domains/documents/hooks/useDrawingViewer";
import { usePermissions } from "@/hooks/usePermissions";
import { useResizablePanelWidth } from "@/hooks/useResizablePanelWidth";
import { useToast } from "@/hooks/use-toast";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { DocumentTranslations } from "@/lib/i18n/types/documents";
import { bulkRestore, downloadDocument } from "@/stores/documents/repository";
import { getContentTypeFromPath } from "@/utils/previewUtils";

interface DocumentHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buildingId: string;
  buildingName: string;
  orgId: string | undefined;
  projectId: number | null;
  projectName: string | null;
  returnFocusRef: RefObject<HTMLButtonElement>;
}

type DocumentHistoryQuery = ReturnType<typeof useDocumentHistory>;
type DocumentHistoryCopy = DocumentTranslations["log"]["history"];

// v1 → v2: the old hook auto-wrote a viewport-sized width 200ms after mount,
// which the new hook would read back as a deliberate preference and pin,
// disabling the responsive default below. A new key discards those.
const HISTORY_PANEL_STORAGE_KEY = "document-history-panel:v2";
const DEFAULT_WIDTH_RATIO = 0.35;

function usePanelFocus(open: boolean, returnFocusRef: RefObject<HTMLButtonElement>) {
  useEffect(() => {
    if (!open) return;
    const returnFocus = returnFocusRef.current;
    return () => {
      if (returnFocus?.isConnected) returnFocus.focus();
    };
  }, [open, returnFocusRef]);
}

function useEscapeClose(open: boolean, onOpenChange: (open: boolean) => void) {
  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const target = event.target as HTMLElement | null;
      if (target?.matches("input, textarea") || target?.isContentEditable) return;
      onOpenChange(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open, onOpenChange]);
}

type ToastFunction = ReturnType<typeof useToast>["toast"];

function useRestoreAction(
  history: DocumentHistoryQuery,
  copy: DocumentHistoryCopy,
  toast: ToastFunction
) {
  const [pendingRestoreId, setPendingRestoreId] = useState<string | null>(null);
  const restore = async (entry: DocumentHistoryEntry) => {
    if (pendingRestoreId) return;
    setPendingRestoreId(entry.documentId);
    try {
      await bulkRestore([entry.documentId]);
      toast({ title: copy.restoreSuccess, description: entry.filename });
      await history.refetch();
    } catch {
      toast({
        title: copy.restoreErrorTitle,
        description: copy.restoreError,
        variant: "destructive",
      });
    } finally {
      setPendingRestoreId(null);
    }
  };
  return { pendingRestoreId, restore };
}

type DocumentViewer = ReturnType<typeof useDocumentViewer>;
type VersionHistorySelection = { versionGroup: string; filename: string } | null;

function useVersionHistory(
  viewer: DocumentViewer,
  buildingId: string,
  openDrawing: (documentId: string, filename: string) => boolean
) {
  const [selection, setSelection] = useState<VersionHistorySelection>(null);
  const show = (entry: DocumentHistoryEntry) => {
    if (!entry.versionGroup) return;
    setSelection({ versionGroup: entry.versionGroup, filename: entry.filename });
  };
  const preview = (documentId: string, filePath: string, filename: string) => {
    if (openDrawing(documentId, filename)) return;
    void viewer.openViewer({
      id: documentId,
      filename,
      file_path: filePath,
      building_id: buildingId,
      content_type: getContentTypeFromPath(filePath),
      size: null,
      created_at: new Date().toISOString(),
    });
  };
  return { selection, show, close: () => setSelection(null), preview };
}

function usePanelActions(
  buildingId: string,
  history: DocumentHistoryQuery,
  copy: DocumentHistoryCopy
) {
  const { toast } = useToast();
  const viewer = useDocumentViewer();
  const restore = useRestoreAction(history, copy, toast);
  // A DWG never goes to the document viewer — its DWG branch rendered the
  // converted child PDF, which this branch removed. Returns true when it took
  // the click; `canOpen` below keeps the row from offering one it can't.
  const { canViewDrawingCompareTool } = usePermissions();
  const { openDrawingViewer } = useDrawingViewer([], undefined, {
    enabled: canViewDrawingCompareTool,
    buildingId,
  });
  const openDrawing = (documentId: string, filename: string) => {
    if (!isDwgParentDoc({ filename })) return false;
    if (canViewDrawingCompareTool) openDrawingViewer([documentId]);
    return true;
  };
  const versions = useVersionHistory(viewer, buildingId, openDrawing);
  // One predicate for both affordances in a row (the eye and the filename), so
  // neither renders as a click that does nothing: a deleted entry has no file
  // left to open, and a DWG needs the drawing-tool grant.
  const canOpen = (entry: DocumentHistoryEntry) =>
    !!entry.filePath &&
    !entry.isDeleted &&
    (!isDwgParentDoc({ filename: entry.filename }) || canViewDrawingCompareTool);
  const open = (entry: DocumentHistoryEntry) => {
    if (!canOpen(entry)) return;
    if (openDrawing(entry.documentId, entry.filename)) return;
    void viewer.openViewer({
      id: entry.documentId,
      filename: entry.filename,
      file_path: entry.filePath,
      building_id: buildingId,
      content_type: entry.contentType,
      size: entry.size,
      created_at: entry.createdAt,
      version: entry.version,
      created_by_name: entry.actor.name,
    });
  };
  const download = (entry: DocumentHistoryEntry) => {
    downloadDocument(entry.documentId, entry.filename, { surface: "version_history" }).catch(() => {
      toast({
        title: copy.downloadErrorTitle,
        description: copy.downloadError,
        variant: "destructive",
      });
    });
  };
  const row = {
    canOpen,
    open,
    download,
    restore: (entry: DocumentHistoryEntry) => void restore.restore(entry),
    versionHistory: versions.show,
  };
  return { viewer, versions, row, pendingRestoreId: restore.pendingRestoreId };
}

function usePanelModel(props: DocumentHistoryDialogProps) {
  const { t, language } = useLanguage();
  const copy = t.documents.log.history;
  const { isReady: isOrganizationReady } = useOrganizationContext();
  const history = useDocumentHistory({
    buildingId: props.buildingId,
    orgId: props.orgId,
    projectId: props.projectId,
    enabled: props.open,
  });
  const filters = useHistoryFilters(history, language, props.open);
  useClearOnScopeChange(props.buildingId, props.projectId, filters.clear);
  const actions = usePanelActions(props.buildingId, history, copy);
  useEscapeClose(props.open, props.onOpenChange);
  const resize = useResizablePanelWidth(
    HISTORY_PANEL_STORAGE_KEY,
    Math.round((typeof window !== "undefined" ? window.innerWidth : 1440) * DEFAULT_WIDTH_RATIO)
  );
  const scopeUnavailable =
    isOrganizationReady && !canFetchDocumentHistory(props.buildingId, props.orgId);
  const view: DocumentHistoryPanelView = {
    entries: filters.entries,
    filteredEntries: filters.filteredEntries,
    hasFilters: filters.hasFilters,
    scopeUnavailable,
    projectScoped: props.projectId !== null,
    pendingRestoreId: actions.pendingRestoreId,
  };
  return { copy, language, history, filters, actions, resize, view };
}

type PanelModel = ReturnType<typeof usePanelModel>;

function panelSubtitle(props: DocumentHistoryDialogProps, copy: DocumentHistoryCopy) {
  const scopeName =
    props.projectId === null ? props.buildingName : props.projectName || `#${props.projectId}`;
  const subtitleTemplate = props.projectId === null ? copy.buildingSubtitle : copy.projectSubtitle;
  const subtitle = subtitleTemplate.replace("{name}", scopeName);
  return subtitle;
}

function ResizeHandle(props: { model: PanelModel }) {
  const { copy, resize } = props.model;
  const active = resize.dragging || resize.hover;
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={copy.resizePanel}
      {...resize.handleProps}
      className="absolute bottom-0 left-0 top-0 z-40 w-1.5 cursor-col-resize touch-none transition-colors"
      style={{ background: active ? "hsl(var(--primary) / 0.4)" : "transparent" }}
    />
  );
}

interface PanelHeaderProps {
  copy: DocumentHistoryCopy;
  subtitle: string;
  onClose: () => void;
}

function PanelHeader(props: PanelHeaderProps) {
  return (
    <header className="flex flex-shrink-0 items-start justify-between gap-4 px-5 py-4">
      <div className="min-w-0">
        <h2
          id="document-history-title"
          className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
        >
          {props.copy.title}
        </h2>
        <p id="document-history-subtitle" className="mt-1 truncate text-sm text-foreground">
          {props.subtitle}
        </p>
      </div>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-none text-muted-foreground hover:text-foreground"
            aria-label={props.copy.close}
            onClick={props.onClose}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{props.copy.close}</TooltipContent>
      </Tooltip>
    </header>
  );
}

// Filtering needs at least two entries; which sections the panel offers is
// the control's decision (only those that can narrow something).
function PanelFilters(props: { model: PanelModel }) {
  const { copy, filters, language } = props.model;
  if (filters.entries.length < 2) return null;
  return (
    <div className="space-y-3 px-5 pb-5 pt-4">
      <div className="relative min-w-0">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          value={filters.query}
          onChange={(event) => filters.setQuery(event.target.value)}
          placeholder={copy.searchPlaceholder}
          aria-label={copy.searchPlaceholder}
          className={`${CONTROL_FIELD_CLASS} pl-8 pr-8`}
        />
      </div>
      <DocumentHistoryFilterControl filters={filters} copy={copy} language={language} />
    </div>
  );
}

function PanelTimeline(props: { model: PanelModel }) {
  const { model } = props;
  const panelActions = { row: model.actions.row, clearFilters: model.filters.clear };
  return (
    <DocumentHistoryPanelBody
      history={model.history}
      view={model.view}
      language={model.language}
      copy={model.copy}
      actions={panelActions}
    />
  );
}

function HistoryPanel(props: { dialog: DocumentHistoryDialogProps; model: PanelModel }) {
  const { dialog, model } = props;
  const close = () => dialog.onOpenChange(false);
  return (
    <aside
      autoFocus
      tabIndex={-1}
      role="dialog"
      aria-modal="false"
      aria-labelledby="document-history-title"
      aria-describedby="document-history-subtitle"
      className="fixed bottom-0 right-0 top-0 z-30 flex flex-col overflow-hidden border-l bg-background"
      style={{ width: model.resize.width, boxShadow: "-18px 0 40px -24px rgba(40,30,15,0.22)" }}
    >
      <ResizeHandle model={model} />
      <PanelHeader copy={model.copy} subtitle={panelSubtitle(dialog, model.copy)} onClose={close} />
      <PanelFilters model={model} />
      <PanelTimeline model={model} />
    </aside>
  );
}

function DocumentViewerOverlay(props: { viewer: DocumentViewer }) {
  return (
    <DocumentViewerDialog
      document={props.viewer.document}
      blobUrl={props.viewer.blobUrl}
      blobData={props.viewer.blobData}
      open={props.viewer.isOpen}
      onOpenChange={props.viewer.setIsOpen}
      originalDwgFilePath={props.viewer.originalDwgFilePath ?? undefined}
    />
  );
}

function VersionHistoryOverlay(props: { versions: ReturnType<typeof useVersionHistory> }) {
  const { versions } = props;
  return (
    <DocumentVersionHistory
      open={versions.selection !== null}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) versions.close();
      }}
      versionGroup={versions.selection?.versionGroup ?? null}
      filename={versions.selection?.filename ?? ""}
      onPreview={versions.preview}
    />
  );
}

function HistoryOverlays(props: { model: PanelModel }) {
  const { viewer, versions } = props.model.actions;
  return (
    <>
      <DocumentViewerOverlay viewer={viewer} />
      <VersionHistoryOverlay versions={versions} />
    </>
  );
}

export function DocumentHistoryDialog(props: DocumentHistoryDialogProps) {
  const model = usePanelModel(props);
  usePanelFocus(props.open, props.returnFocusRef);
  useBottomRightExclusion("document-history-panel", model.resize.settledWidth, props.open);
  if (!props.open) return null;

  return (
    <TooltipProvider>
      <HistoryPanel dialog={props} model={model} />
      <HistoryOverlays model={model} />
    </TooltipProvider>
  );
}
